import { useEffect, useRef } from 'react';
import { MessageRole } from '@/constants';
import type { ChatLevelUp } from '@/types/chat';
import { useLazyGetLevelUpSuggestionsQuery } from '@/api';
import { chatMessagesCache } from './chatMessagesCache';

interface UseInjectLevelUpSuggestionsArgs {
  chatId: string;
  /** From `getChat`. We only inject Level up when the chat is editable. */
  canEdit: boolean | undefined;
  /** True while a chat run is still streaming (driven by `chatMessagesCache.isLoading`). */
  isStreaming: boolean;
  /**
   * Token that flips when chat data has been (re)hydrated for this chatId. Triggers
   * the effect to evaluate whether injection is needed after fresh data arrives.
   */
  chatDataReadyToken: unknown;
}

interface PendingLevelUp {
  chatId: string;
  assistantId: string;
  levelUp: ChatLevelUp[];
}

export const useInjectLevelUpSuggestions = ({
  chatId,
  canEdit,
  isStreaming,
  chatDataReadyToken,
}: UseInjectLevelUpSuggestionsArgs) => {
  const lastSeenChatIdRef = useRef<string | undefined>(undefined);
  const fetchInitiatedForAssistantIdRef = useRef<string | null>(null);
  const pendingLevelUpRef = useRef<PendingLevelUp | null>(null);
  const [triggerGetLevelUpSuggestions] = useLazyGetLevelUpSuggestionsQuery();

  useEffect(() => {
    if (lastSeenChatIdRef.current !== chatId) {
      lastSeenChatIdRef.current = chatId;
      fetchInitiatedForAssistantIdRef.current = null;
      pendingLevelUpRef.current = null;
    }

    if (!chatId || !canEdit) return;

    const tryApplyPendingLevelUp = () => {
      const pending = pendingLevelUpRef.current;
      if (!pending || pending.chatId !== chatId) return;
      if (chatMessagesCache.isLoading(chatId)) return;

      const lastAssistant = [...chatMessagesCache.getMessages(chatId)]
        .reverse()
        .find((m) => m.role === MessageRole.Assistant);
      if (!lastAssistant || lastAssistant.id !== pending.assistantId || lastAssistant.levelUp?.length) {
        pendingLevelUpRef.current = null;
        return;
      }

      const { levelUp } = pending;
      chatMessagesCache.updateMessages(chatId, (prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i -= 1) {
          if (next[i].role !== MessageRole.Assistant) continue;
          if (next[i].id !== pending.assistantId) continue;
          if (next[i].levelUp?.length) return prev;
          next[i] = { ...next[i], levelUp };
          break;
        }
        return next;
      });
      pendingLevelUpRef.current = null;
    };

    const lastAssistant = [...chatMessagesCache.getMessages(chatId)]
      .reverse()
      .find((m) => m.role === MessageRole.Assistant);
    if (!lastAssistant) return;
    if (lastAssistant.levelUp?.length) {
      fetchInitiatedForAssistantIdRef.current = null;
      pendingLevelUpRef.current = null;
      return;
    }

    const assistantId = lastAssistant.id;

    if (fetchInitiatedForAssistantIdRef.current !== assistantId) {
      fetchInitiatedForAssistantIdRef.current = assistantId;
      void triggerGetLevelUpSuggestions()
        .unwrap()
        .then((res) => {
          const levelUp = (res.levelUpSuggestions ?? []).map((prompt) => ({ prompt }));
          if (!levelUp.length) return;
          pendingLevelUpRef.current = { chatId, assistantId, levelUp };
          tryApplyPendingLevelUp();
        })
        .catch((err) => {
          console.warn('Failed to load level-up suggestions:', err);
          if (fetchInitiatedForAssistantIdRef.current === assistantId) {
            fetchInitiatedForAssistantIdRef.current = null;
          }
        });
    }

    if (isStreaming) return;
    tryApplyPendingLevelUp();
  }, [chatId, canEdit, isStreaming, chatDataReadyToken, triggerGetLevelUpSuggestions]);
};
